/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React from 'react'
import { injectIntl } from 'react-intl'
import moment from 'moment'

import {
  Axis,
  BarSeries,
  Chart,
  LineAnnotation,
  Position,
  ScaleType,
  Settings,
} from '@elastic/charts'
import { EuiBadge, EuiFlexGroup, EuiLoadingChart, useEuiTheme } from '@elastic/eui'

import { withErrorBoundary } from '@modules/cui/Boundary'
import { useElasticChartsTheme } from '@modules/cui/charts/hooks'

import useCostTrendsChart from './useCostTrendsChart'
import { getPrettyConsumptionValue } from './utils'
import NoDataAvailableFallback from './NoDataAvailableFallback'
import NoScopeSelectedFallback from './NoScopeSelectedFallback'

import type { WrappedComponentProps } from 'react-intl'
import type { FunctionComponent } from 'react'
import type { BudgetConfigurationValues } from '../../../types'

const COST_VALUE_PRECISION = 4

type Props = {
  amount: BudgetConfigurationValues['amount']
  dateFrom: string
  dateTo: string
  isScopeSelected: boolean
  scopeType: BudgetConfigurationValues['scopeType']
  scopeValues: BudgetConfigurationValues['scopeValues']
}

const CostTrendsChart: FunctionComponent<Props & WrappedComponentProps> = ({
  amount,
  dateFrom,
  dateTo,
  isScopeSelected,
  scopeType,
  scopeValues,
}) => {
  const { euiTheme } = useEuiTheme()
  const baseTheme = useElasticChartsTheme()

  const {
    amountValue,
    usageChartData,
    customYDomain,
    isLoading,
    isError,
    seriesName,
    xDomain,
    yAccessors,
  } = useCostTrendsChart({
    amount,
    dateFrom,
    dateTo,
    scopeType,
    scopeValues,
  })

  if (!isScopeSelected) {
    return <NoScopeSelectedFallback scopeType={scopeType} scopeValues={scopeValues} />
  }

  if (isLoading) {
    return (
      <EuiFlexGroup alignItems='center' justifyContent='center'>
        <EuiLoadingChart
          size='xl'
          data-test-subj='billing.notifications.budgets.budget-settings.cost-trends.chart.loading'
        />
      </EuiFlexGroup>
    )
  }

  const hasFoundNoData = !usageChartData?.length || !yAccessors.length

  if (hasFoundNoData || isError) {
    return <NoDataAvailableFallback isError={isError} />
  }

  const isLineAnnotationVisible = !!amount

  return (
    <Chart size={['100%', '100%']}>
      <Settings baseTheme={baseTheme} xDomain={xDomain} showLegend={false} />

      {isLineAnnotationVisible && (
        <LineAnnotation
          id='budget-amount'
          domainType='yDomain'
          dataValues={[{ dataValue: amountValue }]}
          style={{
            line: {
              strokeWidth: 2,
              stroke: euiTheme.colors.danger,
              dash: [5, 5],
            },
          }}
          marker={
            <EuiBadge color='danger' fill={true}>
              <strong>{amountValue}</strong>
            </EuiBadge>
          }
          markerPosition='left'
        />
      )}

      <Axis
        id='usage'
        domain={customYDomain}
        gridLine={{ visible: true }}
        position={Position.Left}
        ticks={6}
        tickFormat={(d) => getPrettyConsumptionValue(d, COST_VALUE_PRECISION)}
      />
      <Axis
        id='timestamp'
        position={Position.Bottom}
        showOverlappingLabels={false}
        showOverlappingTicks={false}
        ticks={6}
        tickFormat={(d) => moment.unix(d).utc().format('MMM')}
      />

      <BarSeries
        id='total'
        data={usageChartData}
        name={seriesName}
        minBarHeight={1}
        splitSeriesAccessors={[]}
        stackAccessors={['timestamp']}
        xAccessor='timestamp'
        yAccessors={yAccessors}
        xScaleType={ScaleType.Ordinal}
        yScaleType={ScaleType.Linear}
      />
    </Chart>
  )
}

export default injectIntl(withErrorBoundary(CostTrendsChart))
